import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  useCreateRecordMutation,
  useDeleteRecordMutation,
  useUpdateRecordMutation,
} from "../../api/managementApi";
import useUploadRecordFiles from "../../hooks/useUploadRecordFiles";
import FilesInput from "../../components/inputs/FilesInput";
import DownloadFiles from "../../components/inputs/DownloadFiles";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../consts/i18n";
import { DimensionType } from "../../api/dimensionType";
import { useDispatch, useSelector } from "react-redux";
import { removeNonStoredMeasure } from "../../redux/measures2/measures2Reducer";

export const ConfigurePanel = () => {
  const { projectId, clientId, stateId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(namespaces.viewer);
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const dimension = searchParams.get("dimension");
  const panelId = searchParams.get("panelId");
  const isEdit = searchParams.get("edit") === "true";

  const [createRecord, { isLoading: isCreating }] = useCreateRecordMutation();
  const [deleteRecord, { isLoading: isDeleting }] = useDeleteRecordMutation();
  const [updateRecord, { isLoading: isUpdating }] = useUpdateRecordMutation();

  const nonStoredMeasures = useSelector((state) => state.measures2.nonStored);

  const { uploadFiles } = useUploadRecordFiles();

  // controlled form values
  const [panelName, setPanelName] = useState("");
  const [type, setType] = useState("");
  const [data, setData] = useState([]);
  const [position, setPosition] = useState("");
  const [mountingType, setMountingType] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!isEdit && panelId) {
      const currentPanel = nonStoredMeasures.find(
        (measure) => measure.id === panelId,
      );

      if (currentPanel && currentPanel.points) {
        setData(currentPanel.points);

        // Only set panel name if it's currently empty (initial load)
        if (currentPanel.name && panelName === "") {
          setPanelName(currentPanel.name);
        }
      }
    }
  }, [isEdit, panelId, nonStoredMeasures, panelName]);

  /**
   * @param {FormEvent} e
   */
  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (isEdit) {
        if (files && files.length > 0) {
          const res = await uploadFiles(panelId, files);
          if (!res) {
            return;
          }
        }

        await updateRecord({
          id: panelId,
          name: panelName,
          type: "SOLAR_PANEL",
          data,
          photos,
          projectId,
          extra: {
            panelType: type,
            serialNumber,
            anchorType: mountingType,
            positionPvModule: position,
          },
        });
      } else {
        const response = await createRecord({
          name: panelName,
          type: "SOLAR_PANEL",
          data, // Sending NULL will erase the polygon
          projectId,
          extra: {
            panelType: type,
            serialNumber,
            anchorType: mountingType,
            positionPvModule: position,
          },
        });

        const panelData = response.data;

        if (panelData?.data && files.length) {
          for (const file of files) {
            const res = await uploadFiles(panelData.data.id, [file]);
            if (!res) {
              await deleteRecord(panelData.id).unwrap();
              throw new Error("File upload failed");
            }
          }
          // TODO: 🚨 check if right file type & disable button while submitting
        }

        if (panelId) {
          dispatch(removeNonStoredMeasure(panelId));
        }

        setFiles([]);
      }

      // TODO: Abstract this into its own function since it's used in multiple places
      if (dimension === DimensionType.D3) {
        navigate(
          `/clients/${clientId}/projects/${projectId}/pointcloud/${stateId}`,
        );
      } else {
        navigate(`/clients/${clientId}/projects/${projectId}/map/${stateId}`);
      }
    } catch (err) {
      // TODO: 🚨 no designs were given to handle UI
      console.error(err);
    }
  }

  return (
    <section className="bg-secondary h-full">
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-center">
          {isEdit
            ? t("sidebar.panels.editPanel")
            : t("sidebar.panels.newPanel")}
        </h1>
        {/* FORM */}
        <form
          className="mt-4 flex flex-col items-center"
          onSubmit={handleSubmit}
        >
          <div className="min-w-full mb-2">
            <label className="block">
              <span className="text-left font-bold text-xs text-primary">
                {t("sidebar.panels.name")}
              </span>
              <input
                className="mt-1 py-2 px-4 text-sm rounded-md shadow-sm w-full"
                type="text"
                placeholder="XCG60 - 157C"
                required
                value={panelName}
                onChange={(e) => setPanelName(e.target.value)}
              />
            </label>
          </div>
          <div className="min-w-full mb-2">
            <label className="block">
              <span className="text-left font-bold text-xs text-primary">
                {t("sidebar.panels.typeWithId")}
              </span>
              <input
                className="mt-1 py-2 px-4 text-sm rounded-md shadow-sm w-full"
                type="text"
                required
                name="panelType"
                value={type}
                onChange={(e) => setType(e.target.value)}
              />
            </label>
          </div>
          <div className="min-w-full mb-2">
            <label className="block">
              <span className="text-left font-bold text-xs text-primary">
                {t("sidebar.panels.typeOfAttachWithId")}
              </span>
              <input
                className="mt-1 py-2 px-4 text-sm rounded-md shadow-sm w-full"
                type="text"
                required
                name="mountingType"
                value={mountingType}
                onChange={(e) => setMountingType(e.target.value)}
              />
            </label>
          </div>
          <div className="min-w-full mb-2">
            <label className="block">
              <span className="text-left font-bold text-xs text-primary">
                {t("sidebar.panels.pvPosition")}
              </span>
              <input
                className="mt-1 py-2 px-4 text-sm rounded-md shadow-sm w-full"
                type="text"
                name="position"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
              />
            </label>
          </div>
          <div className="min-w-full mb-2">
            <label className="block">
              <span className="text-left font-bold text-xs text-primary">
                {t("sidebar.panels.serialNum")}
              </span>
              <input
                className="mt-1 py-2 px-4 text-sm rounded-md shadow-sm w-full"
                type="text"
                name="serialNumber"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
              />
            </label>
          </div>

          <div className="min-w-full mb-2">
            <label className="block">
              <span className="text-left font-bold text-xs text-primary">
                {t("sidebar.panels.additionalFiles")}
              </span>
            </label>
            <DownloadFiles
              files={photos || []}
              onFilesChange={setPhotos}
              displayFileSizes={true}
              lightTheme={true}
            ></DownloadFiles>
            <div className="h-[13px]"></div>
            <FilesInput
              lightTheme={true}
              files={files || []}
              dropAreaLabel={t("form.dropAreaLabel")}
              onChange={setFiles}
            />
          </div>

          <div className="mt-16 flex gap-6 w-full justify-center">
            <button
              className="py-4 px-12 bg-white text-black font-bold text-xl rounded-lg shadow-sm"
              onClick={() => {
                if (!dimension || !stateId) {
                  return navigate(`/clients/${clientId}/projects/${projectId}`);
                }
                if (dimension === DimensionType.D3) {
                  navigate(
                    `/clients/${clientId}/projects/${projectId}/pointcloud/${stateId}`,
                  );
                } else {
                  navigate(
                    `/clients/${clientId}/projects/${projectId}/map/${stateId}`,
                  );
                }
              }}
            >
              {isEdit ? t("common.cancel") : t("common.throwAway")}
            </button>
            <button
              className="py-4 px-12 bg-primary text-white font-bold text-xl rounded-lg shadow-sm"
              type="submit"
              disabled={isCreating || isDeleting || isUpdating}
            >
              {isEdit ? t("common.save") : t("common.create")}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
